import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Help } from "./Help";

/** Speech recognition of the browser (Chrome, Edge, Safari); free, no extra service. */
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start(): void;
  stop(): void;
}
type RecognitionCtor = new () => Recognition;

const ctor = (): RecognitionCtor | null => {
  const g = globalThis as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return g.SpeechRecognition ?? g.webkitSpeechRecognition ?? null;
};

const SPEECH_LANG: Record<string, string> = {
  hu: "hu-HU", en: "en-GB", de: "de-DE", sk: "sk-SK", cs: "cs-CZ", pl: "pl-PL", ro: "ro-RO", hr: "hr-HR", sl: "sl-SI", "sr-Latn": "sr-RS", uk: "uk-UA",
};

/** Adds dictated text to what is already written, with a space or new sentence between. */
export function appendDictation(current: string, spoken: string): string {
  const s = spoken.trim();
  if (!s) return current;
  const text = s.charAt(0).toLocaleUpperCase() + s.slice(1);
  if (!current.trim()) return text;
  return /[.!?]\s*$/.test(current) ? `${current.trimEnd()} ${text}` : `${current.trimEnd()}, ${s}`;
}

/**
 * Microphone button: tap, speak, tap again (or pause). The recognised text is
 * handed to onText. Hidden where the browser has no speech recognition.
 */
export function Dictate({ onText, label }: { onText: (text: string) => void; label: string }) {
  const { t, i18n } = useTranslation();
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<Recognition | null>(null);
  const C = ctor();
  useEffect(() => () => rec.current?.stop(), []);
  if (!C) return null;

  const toggle = () => {
    setError(null);
    if (listening) {
      rec.current?.stop();
      return;
    }
    const r = new C();
    r.lang = SPEECH_LANG[i18n.language] ?? i18n.language;
    r.continuous = true;
    r.interimResults = false;
    r.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]!;
        if (res.isFinal) onText(res[0].transcript);
      }
    };
    r.onerror = (e) => setError(e.error === "not-allowed" ? t("dictate.notAllowed") : t("dictate.failed"));
    r.onend = () => setListening(false);
    rec.current = r;
    r.start();
    setListening(true);
  };

  return (
    <span className="dictate">
      <button
        type="button"
        className={`mic${listening ? " on" : ""}`}
        onClick={toggle}
        aria-pressed={listening}
        aria-label={`${listening ? t("dictate.stop") : t("dictate.start")}: ${label}`}
      >
        <svg className="gi" viewBox="0 0 48 48" width="24" height="24" aria-hidden="true" focusable="false">
          <rect x="17" y="4" width="14" height="24" style={{ fill: "var(--ico-body, #0E2433)" }} />
          <rect x="21" y="10" width="6" height="4" style={{ fill: "var(--ico-accent, #10B89A)" }} />
          <path d="M10 20v4c0 8 6 13 14 13s14-5 14-13v-4" fill="none" strokeWidth="5" style={{ stroke: "var(--ico-body, #0E2433)" }} />
          <rect x="21.5" y="36" width="5" height="8" style={{ fill: "var(--ico-body, #0E2433)" }} />
        </svg>
        <span>{listening ? t("dictate.listening") : t("dictate.start")}</span>
      </button>
      <Help id="dictate" />
      {error && <span className="error small">{error}</span>}
    </span>
  );
}
